import { createGlobalState } from '@vueuse/core'
import { injectMinorLocales } from 'src/plugins/i18n'
import type {
  ApiUserRole,
  TokenPrecision,
} from 'src/services/json-api/json-api.types'
import {
  API_USER_KIND_TO_USER_ROLE_MAPPER,
  TOKEN_SET_PRECISIONS_TO_AUTH_PRECISION_MAPPER,
} from 'src/services/json-api/json-api.types'
import {
  PERSON_CREATION_TYPES,
  PERSON_CREATION_TYPES_BASE,
} from 'src/services/mappings/person/person.const'
import { computeCurrentAppMode } from 'src/tpbp-core/app-mode'
import { APP_MODES, type AppMode } from 'src/tpbp-core/app-mode/app-mode.types'
import type {
  PersonModel,
  RelationModel,
} from 'src/tpbp-json-api-handler/models/models'
import { computed, ref, watch } from 'vue'
import { getAllUserInfo } from '../../../tpbp-json-api-handler/models/person/current-user-info'
import * as CurrentUserChildrenBusinessLogic from './current-user-children.business-logic'
import * as CurrentUserPartnerBusinessLogic from './current-user-partner.business-logic'
import { getLegalRepresentatives } from './current-user-partner.business-logic'
import * as CurrentUserBusinessLogic from './current-user.business-logic'
import { isPartnerFiscal } from './current-user.business-logic'
import { AGE_MAX } from './current-user.const'
import {
  DEFAULT_CURRENT_USER_STATE,
  type CurrentUserState,
} from './current-user.model'

export const useCurrentUser = createGlobalState(() => {
  const _state = ref<CurrentUserState>(DEFAULT_CURRENT_USER_STATE)
  const _ageMax = ref(AGE_MAX)

  const _currentUserId = computed(() => _state.value.currentUser?.id ?? '')
  const _relations = computed<RelationModel[]>(
    () => _state.value.currentUser._extended_relations ?? [],
  )
  const _currentUserMaritalStatus = computed(
    () => _state.value.currentUser.maritalStatus,
  )

  const _partnerRelation = computed(() =>
    CurrentUserPartnerBusinessLogic.getCurrentUserPartnerRelationAmongAllRelations(
      _relations.value,
    ),
  )

  const _legalRepresentatives = computed(() =>
    getLegalRepresentatives(_relations.value),
  )

  const _currentUserPartner = computed<PersonModel | undefined>(() =>
    CurrentUserPartnerBusinessLogic.getCurrentUserPartner({
      partnerRelationSubject: _partnerRelation.value?._subject,
      partnerRelationRelatedTo: _partnerRelation.value?._relatedTo,
      currentUserId: _currentUserId.value,
    }),
  )

  const _currentUserPartnerRelations = computed<RelationModel[]>(
    () => _currentUserPartner.value?._relations ?? [],
  )

  const _allUserInfo = computed(() =>
    getAllUserInfo(_state.value.currentUser, [
      ..._relations.value,
      ..._currentUserPartnerRelations.value,
    ]),
  )

  function computePartnerTab() {
    return _currentUserHasPartner.value
      ? [
          {
            key: currentUserPartnerId.value,
            label: currentPartnerFullName.value,
          },
        ]
      : []
  }

  const userTabs = computed((): { key: string; label: string }[] =>
    [
      {
        key: _currentUserId.value,
        label: currentUserFullName.value,
      },
      ...computePartnerTab(),
    ].filter((element) => !!element.key && !!element.label),
  )

  const _currentUserHasPartner = computed(() =>
    CurrentUserPartnerBusinessLogic.verifyCurrentUserHasPartner(
      _currentUserMaritalStatus.value,
      _currentUserPartner.value,
    ),
  )

  const currentUserHasPartnerFiscal = computed(() => {
    return (
      _currentUserHasPartner.value &&
      isPartnerFiscal(_currentUserMaritalStatus.value)
    )
  })

  const _userAndPartnerChildren = computed(() =>
    CurrentUserChildrenBusinessLogic.getChildren(
      _state.value.currentUser,
      _relations.value,
      _currentUserPartner.value,
    ),
  )

  const _allChildrenPerson = computed(() =>
    CurrentUserChildrenBusinessLogic.getAllChildrenPerson(
      _state.value.currentUser,
      _relations.value,
      _currentUserPartner.value,
    ),
  )

  const _allPersons = computed(() =>
    CurrentUserBusinessLogic.getAllPersons({
      user: _state.value.currentUser,
      partner: _currentUserPartner.value,
      children: _allChildrenPerson.value,
    }),
  )

  const _allChildren = computed(() => {
    const { userChildren, commonChildren, partnerChildren } =
      _userAndPartnerChildren.value
    return [...commonChildren, ...userChildren, ...partnerChildren].filter(
      Boolean,
    )
  })

  const temporarilyFullAccessCurrentUser = computed<PersonModel>(
    () => _state.value.currentUser,
  )

  const currentUserAge = computed(
    () => temporarilyFullAccessCurrentUser.value?.age ?? 0,
  )

  const currentUserFullName = computed(() => _state.value.currentUser.fullName)
  const currentUserPartnerId = computed(
    () => _currentUserPartner.value?.id ?? '',
  )

  const currentUserPartnerFiscalId = computed(() => {
    return isPartnerFiscal(_currentUserMaritalStatus.value)
      ? currentUserPartnerId.value
      : ''
  })

  const currentPartnerFullName = computed(
    () => _currentUserPartner.value?.fullName ?? '',
  )

  const currentPartnerFiscalFullName = computed(() =>
    isPartnerFiscal(_currentUserMaritalStatus.value)
      ? _currentUserPartner.value?.fullName ?? ''
      : '',
  )

  const currentAppMode = computed(
    (): AppMode => computeCurrentAppMode(_state.value.currentUserAuthDetail),
  )

  const userAvailableCreationTypes = computed(() =>
    currentAppMode.value === APP_MODES.MINOR
      ? PERSON_CREATION_TYPES_BASE
      : PERSON_CREATION_TYPES,
  )

  function setUserAuthDetails(
    role: ApiUserRole,
    precisions: TokenPrecision[],
  ): void {
    _state.value.currentUserAuthDetail.role =
      API_USER_KIND_TO_USER_ROLE_MAPPER[role]
    _state.value.currentUserAuthDetail.precisions = precisions.map(
      (precision) => TOKEN_SET_PRECISIONS_TO_AUTH_PRECISION_MAPPER[precision],
    )
  }

  watch(currentAppMode, (mode: AppMode) => {
    if (
      mode === APP_MODES.MINOR ||
      mode === APP_MODES.ADVISING_REPORT_MINOR_PDF_GENERATOR
    )
      injectMinorLocales()
  })

  return {
    relations: _relations,
    currentUserMaritalStatus: _currentUserMaritalStatus,
    currentUserPartner: _currentUserPartner,
    currentUserHasPartner: _currentUserHasPartner,
    currentUserHasPartnerFiscal,
    currentUserPartnerId,
    currentUserPartnerFiscalId,
    currentPartnerFullName,
    currentPartnerFiscalFullName,
    currentUserFullname: currentUserFullName,
    temporarilyFullAccessCurrentUser,
    currentAppMode,
    userAvailableCreationTypes,
    userIdsForIndicators: computed(() => _state.value.userIdsForIndicators),
    globalConsentForAggregation: computed(
      () => _state.value.currentUser.globalConsentForAggregation ?? false,
    ),
    allChildren: _allChildren,
    allChildrenPerson: _allChildrenPerson,
    allPersons: _allPersons,
    allUserInfo: _allUserInfo,
    userAndPartnerChildren: _userAndPartnerChildren,
    currentUserId: _currentUserId,
    legalRepresentatives: _legalRepresentatives,
    yearsUntilMax: computed(
      () => _ageMax.value - (_state.value.currentUser.age ?? 0),
    ),
    currentUserAge,
    taxIncome: computed(() => _state.value.currentUser.taxIncome),
    taxSocialContributions: computed(
      () => _state.value.currentUser.taxSocialContributions,
    ),
    taxCehr: computed(() => _state.value.currentUser.taxExceptionalHighIncome),
    taxRealEstate: computed(
      () => _state.value.currentUser.taxRealEstateFortune,
    ),
    taxableIncome: computed(() => _state.value.currentUser.taxableIncome),
    taxRateOnMarginalIncome: computed(
      () => _state.value.currentUser.taxRateOnMarginalIncome,
    ),
    taxDataUpdatedAt: computed(() => _state.value.currentUser.taxDataUpdatedAt),
    isSelfCare: computed(() =>
      CurrentUserBusinessLogic.verifyIsSelfcare(
        _state.value.currentUserAuthDetail.role,
      ),
    ),
    isAdvisor: computed(() =>
      CurrentUserBusinessLogic.verifyIsAdvisor(
        _state.value.currentUserAuthDetail.role,
      ),
    ),
    hasLinxo: computed(() =>
      CurrentUserBusinessLogic.verifyHasLinxo(
        _state.value.currentUserAuthDetail.role,
      ),
    ),
    hasDependentChildren: computed((): boolean =>
      _allChildrenPerson.value.some(
        (child) => child.financiallyDependentFrom === _currentUserId.value,
      ),
    ),
    isCurrentUserSelectedInDashboard: computed(() =>
      CurrentUserBusinessLogic.isCurrentUserSelectedInDashboard({
        userIdsForIndicators: _state.value.userIdsForIndicators,
        currentUserId: _currentUserId.value,
      }),
    ),
    householdPersons: computed(() =>
      CurrentUserPartnerBusinessLogic.getHouseholdPersons({
        currentUser: _state.value.currentUser,
        currentUserHasPartner: _currentUserHasPartner.value,
        currentUserPartner: _currentUserPartner.value,
      }),
    ),
    userFullNames: computed(() =>
      CurrentUserBusinessLogic.getUsersFullnames(_allPersons.value),
    ),
    userTabs,
    mustFollowAdvice: computed((): boolean =>
      CurrentUserBusinessLogic.mustFollowAdvice(
        _state.value.currentUserAuthDetail.precisions,
      ),
    ),

    setCurrentUser(newUser: PersonModel): void {
      _state.value.currentUser = newUser
    },
    setUserAuthDetails,
    setUserIdsForIndicators(newUserIds: string[]) {
      _state.value.userIdsForIndicators = newUserIds
    },
    setGlobalConsentForAggregation(
      newGlobalConsentForAggregation: boolean,
    ): void {
      _state.value.currentUser.globalConsentForAggregation =
        newGlobalConsentForAggregation
    },
    getRelationByUserId(id: string) {
      return _relations.value.find((relation) => relation?._subject.id === id)
    },
    getPersonFromId: (id: string) => {
      return id === _currentUserId.value
        ? temporarilyFullAccessCurrentUser.value
        : CurrentUserBusinessLogic.getPersonFromId(_relations.value, id)
    },
  }
})

export type UseCurrentUserFunction = typeof useCurrentUser

export type CurrentUserStore = ReturnType<typeof useCurrentUser>
